import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { T } from '@territory/tokens';
import { useStyleOnce, REDUCED_MOTION } from '../internal/useStyleOnce.js';
import { usePrefersReducedMotion } from '../internal/usePrefersReducedMotion.js';
import { ICONS, resolveIconName, type IconAlias, type IconDef, type IconName, type IconNode } from './icons.js';

export interface IconProps {
  /* Dictionary glyph. Wins over `d` when both are set. */
  name?: IconName | IconAlias;
  /* Raw 24x24 path, for product mocks that keep their own vocabulary. */
  d?: string;
  size?: number;
  /* Stroke width. */
  weight?: number;
  color?: string;
  fill?: string;
  /* Gold pulsing disclosure language on chevRight/chevDown; false for back, close and action chevrons. */
  disclose?: boolean;
  /* Makes the icon meaningful (role="img"); omitted, the icon is decorative. */
  title?: string;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

/* verbatim wedge, off-scale */
const DISCLOSE_PULSE_MS = 2400;

const CSS = `@keyframes rvDisclosePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
${REDUCED_MOTION} { [data-rv-disclose] { animation: none !important; } }`;

const DISCLOSE_GLYPHS: ReadonlySet<IconName> = new Set<IconName>(['chevRight', 'chevDown']);

const parts = (def: IconDef | undefined): { d?: string; solid?: string; nodes?: readonly IconNode[] } =>
  typeof def === 'string' ? { d: def } : def ?? {};

const renderNode = (n: IconNode, i: number, ink: string): ReactNode => {
  const paint = n.solid ? { fill: ink, stroke: 'none' } : n.fill ? { fill: ink } : {};
  if ('path' in n) return <path key={i} d={n.path} {...paint} />;
  if ('circle' in n) return <circle key={i} cx={n.circle[0]} cy={n.circle[1]} r={n.circle[2]} {...paint} />;
  const [x, y, width, height, rx] = n.rect;
  return <rect key={i} x={x} y={y} width={width} height={height} rx={rx} {...paint} />;
};

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, d, size = 22, weight = 1.7, color, fill = 'none', disclose, title, className, style, 'data-testid': testId },
  ref,
) {
  useStyleOnce('icon', CSS);
  const reduced = usePrefersReducedMotion();
  const glyph = name ? resolveIconName(name) : undefined;
  const path = glyph ? parts(ICONS[glyph]) : { d };
  const disclosing = glyph !== undefined && DISCLOSE_GLYPHS.has(glyph) && disclose !== false;
  const ink = color ?? (disclosing ? T.gold : 'currentColor');
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const, focusable: 'false' as const };

  const svg = (
    <svg
      ref={ref}
      data-rv-icon={glyph ?? ''}
      data-testid={testId}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={ink}
      strokeWidth={weight}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      {...a11y}
    >
      {title ? <title>{title}</title> : null}
      {path.nodes ? path.nodes.map((n, i) => renderNode(n, i, ink)) : null}
      {path.d ? <path d={path.d} /> : null}
      {path.solid ? <path d={path.solid} fill={ink} stroke="none" /> : null}
    </svg>
  );

  if (!disclosing) return svg;
  return (
    <span
      data-rv-disclose=""
      style={{
        display: 'inline-flex',
        animation: reduced ? 'none' : `rvDisclosePulse ${DISCLOSE_PULSE_MS}ms ease-in-out infinite`,
      }}
    >
      {svg}
    </span>
  );
});
